import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ExternalLink, Megaphone, Pencil, Plus, Save, Search, Tags, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { api, ApiError, type Topic, type UserApp } from '@/lib/api'

export default function Topics() {
  const [topics, setTopics] = useState<Topic[]>([])
  const [apps, setApps] = useState<UserApp[]>([])
  const [loading, setLoading] = useState(true)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [minFollowers, setMinFollowers] = useState(500)
  const [searchQuery, setSearchQuery] = useState('')
  const [promotedAppId, setPromotedAppId] = useState('')
  const [busy, setBusy] = useState(false)

  const [editing, setEditing] = useState<Topic | null>(null)

  const appById = useMemo(() => {
    const m = new Map<string, UserApp>()
    for (const a of apps) m.set(a.id, a)
    return m
  }, [apps])

  async function load() {
    setLoading(true)
    try {
      const [t, a] = await Promise.all([api.listTopics(), api.listApps()])
      setTopics(t)
      setApps(a)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  async function create() {
    setBusy(true)
    try {
      await api.createTopic({
        name,
        description: description || undefined,
        minFollowers,
        searchQuery: searchQuery || undefined,
        promotedAppId: promotedAppId || null,
      })
      setName('')
      setDescription('')
      setSearchQuery('')
      setPromotedAppId('')
      load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create topic')
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this topic? Captured tweets keep their reference.')) return
    await api.deleteTopic(id)
    load()
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Topics</h1>
        <p className="text-muted-foreground">
          Verticals you're targeting. Claude classifies scraped tweets against these topics and
          decides which to engage with. Each topic can promote exactly one of{' '}
          <Link to="/app/settings" className="text-primary hover:underline">your apps</Link>{' '}
          — Claude will only mention that app in replies to matching tweets.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">New topic</CardTitle>
          <CardDescription>e.g., "Cron job failures", "Divorce questions", "Indie AI tools"</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="name" className="text-xs">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="min" className="text-xs">Min followers (advisory)</Label>
              <Input
                id="min"
                type="number"
                min={0}
                value={minFollowers}
                onChange={(e) => setMinFollowers(Math.max(0, Number(e.target.value)))}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="desc" className="text-xs">Description</Label>
            <Textarea
              id="desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who you're trying to engage. Claude uses this to decide if a tweet fits."
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="q" className="text-xs">X search query (optional)</Label>
            <Input
              id="q"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='e.g., (indie OR bootstrapped) "AI tools" -job lang:en min_faves:5'
            />
            <p className="text-[11px] text-muted-foreground">
              Defaults to the topic name. Supports X's advanced search operators.{' '}
              <a href="https://github.com/igorbrigadir/twitter-advanced-search" target="_blank" rel="noreferrer" className="text-primary hover:underline">Cheat sheet</a>.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="promo" className="text-xs">Promote this app in replies (optional)</Label>
            <select
              id="promo"
              value={promotedAppId}
              onChange={(e) => setPromotedAppId(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— none, pure engagement —</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {apps.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                No apps yet. Add one on{' '}
                <Link to="/app/settings" className="text-primary hover:underline">Settings → My apps</Link>{' '}
                first if you want to promote.
              </p>
            )}
          </div>
          <Button onClick={create} disabled={busy || !name.trim()}>
            <Plus className="h-4 w-4" />
            Create topic
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && topics.length === 0 && (
          <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
            No topics yet.
          </p>
        )}
        {topics.map((t) => {
          const q = (t.searchQuery ?? t.name).trim()
          const searchUrl = `https://x.com/search?q=${encodeURIComponent(q)}&f=live`
          const promoted = t.promotedAppId ? appById.get(t.promotedAppId) : null
          return (
            <div key={t.id} className="flex items-start justify-between gap-4 rounded-md border bg-card px-4 py-3">
              <div className="flex min-w-0 items-start gap-3">
                <Tags className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{t.name}</span>
                    {promoted ? (
                      <span className="inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-[10px] font-medium text-accent-foreground">
                        <Megaphone className="h-2.5 w-2.5" />
                        promotes {promoted.name}
                      </span>
                    ) : (
                      <span className="rounded border border-dashed px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        no promotion
                      </span>
                    )}
                  </div>
                  {t.description && <p className="mt-0.5 text-sm text-muted-foreground">{t.description}</p>}
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Min {t.minFollowers} followers
                    {t.searchQuery && <> · query: <code className="rounded bg-muted px-1">{t.searchQuery}</code></>}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button asChild size="sm" variant="secondary">
                  <a href={searchUrl} target="_blank" rel="noreferrer">
                    <Search className="h-3.5 w-3.5" />
                    Search X
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </a>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(t)} title="Edit topic">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <button
                  onClick={() => remove(t.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Delete topic"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <EditTopicDialog
        topic={editing}
        apps={apps}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load() }}
      />
    </div>
  )
}

function EditTopicDialog({
  topic,
  apps,
  onClose,
  onSaved,
}: {
  topic: Topic | null
  apps: UserApp[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [minFollowers, setMinFollowers] = useState(500)
  const [searchQuery, setSearchQuery] = useState('')
  const [promotedAppId, setPromotedAppId] = useState<string>('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!topic) return
    setName(topic.name)
    setDescription(topic.description ?? '')
    setMinFollowers(topic.minFollowers)
    setSearchQuery(topic.searchQuery ?? '')
    setPromotedAppId(topic.promotedAppId ?? '')
  }, [topic])

  async function save() {
    if (!topic) return
    setBusy(true)
    try {
      await api.updateTopic(topic.id, {
        name,
        description,
        minFollowers,
        searchQuery,
        promotedAppId: promotedAppId || null,
      })
      toast.success('Topic saved')
      onSaved()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={topic !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit topic</DialogTitle>
          <DialogDescription>Changes apply to future classifications and drafts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="e-name" className="text-xs">Name</Label>
              <Input id="e-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="e-min" className="text-xs">Min followers</Label>
              <Input
                id="e-min"
                type="number"
                min={0}
                value={minFollowers}
                onChange={(e) => setMinFollowers(Math.max(0, Number(e.target.value)))}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-desc" className="text-xs">Description</Label>
            <Textarea
              id="e-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-q" className="text-xs">X search query</Label>
            <Input id="e-q" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-promo" className="text-xs">Promote this app in replies</Label>
            <select
              id="e-promo"
              value={promotedAppId}
              onChange={(e) => setPromotedAppId(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— none, pure engagement —</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !name.trim()}>
            <Save className="h-4 w-4" />
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
